/*
 * Copyright (c) 2023
 *
 * The source code and all documents related to the source code ("Material") are owned by
 * Frictionless Systems, LLC.
 *
 * The Material contains trade secrets, proprietary information, and confidential information.
 * No part of the Material may be used, copied, reproduced, modified, published, distributed, or
 * disclosed in any way without Frictionless Systems, LLC’s express prior written permission.
 *
 * The Material may be protected under patent, trademark, copyright, or trade secret laws.  No
 * license is granted to you by disclosure or delivery of the Materials.  Any license must be
 * express and approved by Frictionless Systems, LLC in writing.
 *
 * “Frictionless Systems” and “Impulse” are registered trademarks of Frictionless Systems, LLC.
 */

package systems.frictionless.impulse.rest.model

import com.benasher44.uuid.Uuid
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import systems.frictionless.impulse.rest.internal.UuidSerializer
import systems.frictionless.impulse.rest.model.Question.Type.MultipleChoice.Companion.MINIMUM_LIST_SIZE

/**
 * A question to be presented to a [User] during an [Assessment].
 *
 * Note: The JSON serializer needs `useArrayPolymorphism = false`.
 *
 * @param id The identifier of the question.  This will be associated with the [Answer].
 * @param text The translation of the question to be displayed to the user.
 * @param hint The translation of optional sub-text to further explain the question or context of the question if necessary.
 * This is separated from the question to allow a user experience to display the hint differently (e.g. italics).
 * @param type The type of question, which also encapsulates how the user will respond to the question with an answer.
 */
@kotlinx.serialization.Serializable
data class Question(
    @Serializable(with = UuidSerializer::class) val id: Uuid,
    val text: String,
    val hint: String?,
    val type: Type
) {
    /**
     * Type of question.  Corresponding classes exit for [Answer.Type].
     */
    @Serializable
    sealed class Type {
        /**
         * @param choices The list of choices to be displayed to the user.  The ordering in the list is preserved.
         * The list must have at least [MINIMUM_LIST_SIZE] items in it.
         */
        @Serializable
        @SerialName("mc")
        data class MultipleChoice(val choices: List<String>) : Type() {
            init {
                requireMinimumSize(choices, MINIMUM_LIST_SIZE)
            }

            companion object {
                const val MINIMUM_LIST_SIZE = 2
            }
        }

        @Serializable
        @SerialName("likert")
        data class Likert(val form: Form) : Type() {
            @Serializable
            enum class Form {
                Agreement,
                Frequency
            }
        }

        /**
         * A free-response question solely utilizes the [Answer.comment] field.
         */
        @Serializable
        @SerialName("fr")
        data class FreeResponse(val text: String) : Type()
    }

    companion object
}

fun <T> requireMinimumSize(
    collection: Collection<T>,
    minimumSize: Int
) {
    require(minimumSize > 0) { "expectedSize must be > 0 but was $minimumSize" }

    val collectionSize = collection.size
    require(collectionSize >= minimumSize) { "List has size $collectionSize but expected size >= $minimumSize " }
}
